import { useEffect, useRef, useState, useCallback, useLayoutEffect, KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchCreatePanelPreviews, CreatePanelPreviews } from '../../lib/createPanel'
import { ItemCard } from '../contributions/ItemCard'

// The real discover-feed card, rendered inert and auto-scaled to fit the
// preview frame — so the panel shows exactly what the feed shows.
function MiniItemCard({ card }: { card: Record<string, any> }) {
  const innerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState<number | null>(null)
  const NATURAL_WIDTH = 300

  useLayoutEffect(() => {
    const el = innerRef.current
    if (!el) return
    const measure = () => {
      const h = el.offsetHeight
      if (h > 0) setScale(Math.min(1, PREVIEW_HEIGHT / h))
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div className="w-full h-full flex items-center justify-center overflow-hidden pointer-events-none select-none" aria-hidden="true">
      <div
        ref={innerRef}
        style={{
          width: NATURAL_WIDTH,
          flexShrink: 0,
          transform: scale !== null ? `scale(${scale})` : undefined,
          transformOrigin: 'center center',
          visibility: scale === null ? 'hidden' : 'visible',
        }}
      >
        <ItemCard
          item={{
            id: card.id,
            title: card.title,
            brand: card.brand,
            cached_image_url: card.cached_image_url,
            image_url: card.image_url,
            source_url: card.source_url,
            comment: card.comment,
            created_at: card.created_at,
            saves_count: card.saves_count || 0,
            comments_count: 0,
            user: card.user,
          } as any}
        />
      </div>
    </div>
  )
}

// The Create picker — design ref: Community Asks.dc.html §8a (web panel) and
// §8b (mobile sheet). One component, two variants, same three options in the
// same order on every page. The panel never collapses to a compact list.

const MAROON = '#57241F'

const SURFACE = '#fdfbf4'
const SURFACE_BORDER = '#ddd8c6'
const CARD_BG = '#f3f1e6'
const CARD_BORDER = '#e6e2d0'
const CARD_HOVER_BG = '#F1EDE2'
const CARD_HOVER_BORDER = '#DED2BC'
const PREVIEW_BG = '#E4DECF'
const PREVIEW_HEIGHT = 240
const PANEL_WIDTH = 744
const SCRIM = 'rgba(28,26,23,.28)'
const TITLE_INK = '#181410'
const DESC_INK = '#6f6a60'
const GLYPH_INK = '#3A342C'
const PLUS_GLYPH = '#f7f4ea'

// Shipped nav glyphs from the design canvas — an option is drawn with the icon
// of the place it lives.
function DiscoverGlyph({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.6" fill={GLYPH_INK} />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.6" fill={GLYPH_INK} />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.6" fill={GLYPH_INK} />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.6" fill={GLYPH_INK} />
    </svg>
  )
}

function CurationsGlyph({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3.2 21 8l-9 4.8L3 8z" fill={GLYPH_INK} />
      <path d="M4.6 12 12 15.9 19.4 12" stroke={GLYPH_INK} strokeWidth="1.9" fill="none" strokeLinecap="round" />
      <path d="M4.6 16 12 19.9 19.4 16" stroke={GLYPH_INK} strokeWidth="1.9" fill="none" strokeLinecap="round" />
    </svg>
  )
}

function DiscussGlyph({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 5.5h16v10.5H12l-4.5 3.5V16H4z" fill={GLYPH_INK} />
    </svg>
  )
}

interface CardDef {
  key: 'item' | 'curation' | 'ask'
  href: string
  title: string
  description: string
  glyph: (props: { size?: number }) => React.ReactElement
  sheetSubtitle: string
}

const CARDS: CardDef[] = [
  {
    key: 'item',
    href: '/contribute',
    title: 'Share an item',
    description: 'Something worth sharing with the community',
    glyph: DiscoverGlyph,
    sheetSubtitle: 'A product worth passing on',
  },
  {
    key: 'curation',
    href: '', // resolved per-user (My Curations)
    title: 'Start a Curation',
    description: 'Your own collection of finds.',
    glyph: CurationsGlyph,
    sheetSubtitle: 'Group items around an idea',
  },
  {
    key: 'ask',
    href: '/discussions?compose=ask',
    title: 'Ask the founders',
    description: 'Julia & Elizabeth answer in public',
    glyph: DiscussGlyph,
    sheetSubtitle: 'They answer in public',
  },
]

// Stepped three-up: outer images at 76% height, centre at 100%. With fewer
// images, keep the centre-taller shape.
function steppedHeights(count: number): number[] {
  if (count >= 3) return [76, 100, 76]
  if (count === 2) return [76, 100]
  return [100]
}

interface CreatePanelProps {
  open: boolean
  username: string | null
  // (returnFocus) → header moves focus back to the CREATE trigger
  onClose: (returnFocus: boolean) => void
  // The CREATE button; the dropdown right-aligns to it
  anchorRef?: React.RefObject<HTMLButtonElement | null>
  // Hide sub-copy for a title-only, denser menu
  showDescriptions?: boolean
  // Toggle the scrim behind the dropdown
  dimBackdrop?: boolean
}

export function CreatePanel({
  open,
  username,
  onClose,
  anchorRef,
  showDescriptions = true,
  dimBackdrop = true,
}: CreatePanelProps) {
  const navigate = useNavigate()
  const [previews, setPreviews] = useState<CreatePanelPreviews | null>(null)
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set())
  const [rightOffset, setRightOffset] = useState(24)
  const rootRef = useRef<HTMLDivElement>(null)
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([])

  // Right-align the dropdown to the CREATE button (falls back to 24px inset).
  useLayoutEffect(() => {
    if (!open) return
    const compute = () => {
      const anchor = anchorRef?.current
      const host = rootRef.current?.parentElement
      if (anchor && host) {
        const hostRight = host.getBoundingClientRect().right
        const anchorRight = anchor.getBoundingClientRect().right
        setRightOffset(Math.max(12, Math.round(hostRight - anchorRight)))
      }
    }
    compute()
    window.addEventListener('resize', compute)
    return () => window.removeEventListener('resize', compute)
  }, [open, anchorRef])

  // Resolve previews eagerly so the panel has imagery on first open.
  useEffect(() => {
    let mounted = true
    fetchCreatePanelPreviews()
      .then(data => { if (mounted) setPreviews(data) })
      .catch(err => console.error('Create panel previews failed:', err))
    return () => { mounted = false }
  }, [])

  // Click-outside dismiss (desktop panel; the sheet has its own backdrop).
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const root = rootRef.current
      if (!root) return
      const target = e.target as Node
      // The trigger toggles itself; anything else outside the panel closes it.
      if (!root.contains(target) && !(target as HTMLElement).closest?.('[data-create-trigger]')) {
        onClose(false)
      }
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open, onClose])

  // Focus the first card when the menu opens.
  useEffect(() => {
    if (open) itemRefs.current[0]?.focus()
  }, [open])

  const markFailed = useCallback((url: string) => {
    setFailedImages(prev => new Set(prev).add(url))
  }, [])

  const cardHref = (card: CardDef): string =>
    card.key === 'curation' ? (username ? `/profile/${username}` : '/') : card.href

  const activate = (card: CardDef, e: ReactMouseEvent | null) => {
    onClose(false)
    if (card.key === 'curation') {
      navigate(cardHref(card), { state: { openNewCuration: true } })
    } else if (card.key === 'item') {
      // The share composer is home-route view state (what SHARE always did),
      // not the bare /contribute route.
      navigate('/', { state: { view: 'contribute' } })
    } else {
      navigate(card.href)
    }
  }

  type ReactMouseEvent = React.MouseEvent<HTMLAnchorElement>

  const onCardClick = (card: CardDef) => (e: ReactMouseEvent) => {
    // Plain left-click becomes SPA navigation; modified clicks and middle-click
    // fall through to the real href.
    if (e.button === 0 && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) {
      e.preventDefault()
      activate(card, e)
    }
  }

  const onMenuKeyDown = (e: ReactKeyboardEvent) => {
    const items = itemRefs.current.filter(Boolean) as HTMLAnchorElement[]
    const idx = items.findIndex(el => el === document.activeElement)
    if (e.key === 'Escape') {
      e.preventDefault()
      onClose(true)
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
      e.preventDefault()
      items[(idx + 1 + items.length) % items.length]?.focus()
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
      e.preventDefault()
      items[(idx - 1 + items.length) % items.length]?.focus()
    }
  }

  const usable = (url: string | undefined | null): url is string =>
    !!url && !failedImages.has(url)

  // ---- Preview renderers (shared by panel and sheet at different scales) ----

  const itemPreview = (compact: boolean) => {
    const img = previews?.latestItem
    if (img && usable(img.imageUrl)) {
      // Panel: contain, so the whole product shows on the tint instead of a
      // tight crop. Sheet thumbnails stay cover at 58px.
      if (compact) {
        return (
          <img
            src={img.imageUrl}
            alt=""
            onError={() => markFailed(img.imageUrl)}
            className="w-full h-full object-cover"
          />
        )
      }
      // Panel: the real discover-feed card, miniaturized to fit the frame.
      return <MiniItemCard card={img.card} />
    }
    // TODO(evergreen): replace glyph fallback with a bundled evergreen photo
    // asset per card. Height is preserved either way — no layout shift.
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: PREVIEW_BG }}>
        <span style={{ opacity: 0.35 }}><DiscoverGlyph size={compact ? 20 : 34} /></span>
      </div>
    )
  }

  const curationPreview = (compact: boolean) => {
    const items = (previews?.latestCuration?.items || []).filter(i => usable(i.imageUrl))
    if (items.length === 0) {
      return (
        <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: PREVIEW_BG }}>
          <span style={{ opacity: 0.35 }}><CurationsGlyph size={compact ? 20 : 34} /></span>
        </div>
      )
    }
    if (compact) {
      // Sheet thumbnail keeps the stepped three-up at row scale.
      const heights = steppedHeights(Math.min(items.length, 3))
      return (
        <div
          className="w-full h-full flex items-end justify-center"
          style={{ backgroundColor: PREVIEW_BG, gap: 3, padding: 6 }}
        >
          {items.slice(0, 3).map((it, i) => (
            <div key={i} style={{ width: '32%', height: `${heights[i]}%`, borderRadius: 3, overflow: 'hidden' }}>
              <img
                src={it.imageUrl}
                alt=""
                onError={() => markFailed(it.imageUrl)}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
      )
    }
    // Panel: 2×2 grid of the curation's first four items.
    return (
      <div
        className="w-full h-full"
        style={{
          backgroundColor: PREVIEW_BG,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gridAutoRows: '1fr',
          gap: 8,
          padding: 12,
        }}
      >
        {items.slice(0, 4).map((it, i) => (
          <div key={i} style={{ overflow: 'hidden', minHeight: 0 }}>
            <img
              src={it.imageUrl}
              alt=""
              onError={() => markFailed(it.imageUrl)}
              className="w-full h-full object-cover"
            />
          </div>
        ))}
      </div>
    )
  }

  const founderCircle = (
    f: { name: string; avatarUrl: string | null },
    i: number,
    size: number,
    ring: string,
    overlap: number
  ) => (
    <span
      key={`${f.name}-${i}`}
      className="rounded-full overflow-hidden flex-shrink-0 relative"
      style={{
        width: size,
        height: size,
        border: `${size > 50 ? 3 : 2}px solid ${ring}`,
        marginLeft: i === 0 ? 0 : overlap,
        // Later founders stack on top (Elizabeth over Julia)
        zIndex: i + 1,
        backgroundColor: '#D8C3B4',
      }}
    >
      {f.avatarUrl ? (
        <img src={f.avatarUrl} alt="" className="w-full h-full object-cover" />
      ) : (
        <span
          className="w-full h-full flex items-center justify-center font-serif font-semibold"
          style={{ color: '#5A2A2A', fontSize: size * 0.4 }}
        >
          {f.name[0]?.toUpperCase()}
        </span>
      )}
    </span>
  )

  const foundersPreview = () => {
    const founders = previews?.founders || []
    const display = founders.length > 0 ? founders : [{ name: 'J', avatarUrl: null }, { name: 'E', avatarUrl: null }]
    return (
      <div
        className="w-full h-full flex flex-col items-center justify-center"
        style={{ backgroundColor: PREVIEW_BG, gap: 12 }}
      >
        <div className="flex items-center">
          {display.map((f, i) => founderCircle(f, i, 66, PREVIEW_BG, -20))}
        </div>
        {founders.length > 0 && (
          <div
            className="uppercase"
            style={{ font: '600 11px/1 -apple-system, BlinkMacSystemFont, sans-serif', letterSpacing: '.11em', color: DESC_INK }}
          >
            {founders.map(f => f.name).join(' & ')}
          </div>
        )}
      </div>
    )
  }

  const foundersSheetPreview = () => {
    const founders = previews?.founders || []
    const display = founders.length > 0 ? founders : [{ name: 'J', avatarUrl: null }, { name: 'E', avatarUrl: null }]
    return (
      <div className="w-full h-full flex items-center justify-center">
        {display.map((f, i) => founderCircle(f, i, 38, CARD_HOVER_BG, -13))}
      </div>
    )
  }

  const previewFor = (key: CardDef['key'], compact: boolean) => {
    if (key === 'item') return itemPreview(compact)
    if (key === 'curation') return curationPreview(compact)
    return compact ? foundersSheetPreview() : foundersPreview()
  }

  if (!open) return null

  return (
    <div ref={rootRef}>
      {/* Desktop: scrim (feed stays visible beneath), dropdown anchored to CREATE */}
      {dimBackdrop && (
        <div
          className="hidden lg:block fixed inset-x-0 bottom-0 top-20 motion-safe:animate-[createDimIn_300ms_ease]"
          style={{ backgroundColor: SCRIM }}
          onClick={() => onClose(false)}
        />
      )}
      <div
        className="hidden lg:block absolute motion-safe:animate-[createPanelIn_300ms_cubic-bezier(0.22,1,0.36,1)]"
        role="menu"
        aria-label="Create"
        onKeyDown={onMenuKeyDown}
        style={{
          top: 'calc(100% + 10px)',
          right: rightOffset,
          width: PANEL_WIDTH,
          backgroundColor: SURFACE,
          border: `1px solid ${SURFACE_BORDER}`,
          boxShadow: '0 18px 40px rgba(28,26,23,.16)',
          padding: 14,
        }}
      >
        <style>{`
          @keyframes createPanelIn { from { opacity: 0; transform: translateY(-8px); } to { opacity: 1; transform: none; } }
          @keyframes createDimIn { from { opacity: 0; } to { opacity: 1; } }
        `}</style>
        {/* minmax(0,1fr): the mini item card's natural 300px layout box must
            not widen its column — all three boxes stay identical. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
          {CARDS.map((card, i) => (
            <a
              key={card.key}
              ref={el => { itemRefs.current[i] = el }}
              role="menuitem"
              href={cardHref(card)}
              onClick={onCardClick(card)}
              className="block group focus:outline-none"
              style={{
                border: `1px solid ${CARD_BORDER}`,
                borderRadius: 0,
                padding: 10,
                height: '100%',
                backgroundColor: CARD_BG,
                transition: 'background-color 120ms ease, border-color 120ms ease',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = CARD_HOVER_BG
                e.currentTarget.style.borderColor = CARD_HOVER_BORDER
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = CARD_BG
                e.currentTarget.style.borderColor = CARD_BORDER
              }}
              onFocus={e => { e.currentTarget.style.outline = `2px solid ${MAROON}`; e.currentTarget.style.outlineOffset = '2px' }}
              onBlur={e => { e.currentTarget.style.outline = 'none' }}
            >
              {/* Transparent wrapper: each preview owns its own background, so
                  a hugged item image shows no tint border. */}
              <div style={{ height: PREVIEW_HEIGHT, borderRadius: 0, overflow: 'hidden', marginBottom: 10, position: 'relative' }}>
                {previewFor(card.key, false)}
                {/* Click affordance: maroon circle + plus, bottom-right */}
                <span
                  aria-hidden="true"
                  className="absolute flex items-center justify-center rounded-full"
                  style={{
                    right: 9,
                    bottom: 9,
                    width: 23,
                    height: 23,
                    backgroundColor: MAROON,
                    boxShadow: '0 2px 6px rgba(40,26,22,.28)',
                  }}
                >
                  <svg width="11" height="11" viewBox="0 0 22 22">
                    <path d="M11 4v14M4 11h14" stroke={PLUS_GLYPH} strokeWidth="2.2" strokeLinecap="round" />
                  </svg>
                </span>
              </div>
              <div className="flex items-center" style={{ gap: 8, marginBottom: showDescriptions ? 3 : 0 }}>
                <card.glyph size={15} />
                <span style={{ font: '600 14px/1.2 -apple-system, BlinkMacSystemFont, sans-serif', color: TITLE_INK }}>
                  {card.title}
                </span>
              </div>
              {showDescriptions && (
                // Fixed two-line slot so box height is identical across cards
                // regardless of how the copy wraps.
                <div style={{ font: '400 12.5px/1.4 -apple-system, BlinkMacSystemFont, sans-serif', color: DESC_INK, minHeight: 35 }}>
                  {card.description}
                </div>
              )}
            </a>
          ))}
        </div>
      </div>

      {/* Mobile: bottom sheet with dimmed backdrop */}
      <div className="lg:hidden fixed inset-0 z-50">
        <div
          className="absolute inset-0"
          style={{ backgroundColor: 'rgba(30,20,16,.18)' }}
          onClick={() => onClose(false)}
        />
        <div
          role="menu"
          aria-label="Create"
          onKeyDown={onMenuKeyDown}
          className="absolute left-0 right-0 bottom-0 motion-safe:animate-[createSheetIn_140ms_ease-out]"
          style={{
            backgroundColor: SURFACE,
            borderRadius: '30px 30px 0 0',
            padding: '14px 20px 34px',
            boxShadow: '0 -20px 40px -20px rgba(0,0,0,.25)',
          }}
        >
          <style>{`@keyframes createSheetIn { from { transform: translateY(24px); opacity: .6; } to { transform: none; opacity: 1; } }`}</style>
          <div style={{ width: 42, height: 5, borderRadius: 999, backgroundColor: '#D9D2C2', margin: '2px auto 20px' }} />
          <div style={{ fontFamily: 'Canela, Georgia, serif', fontWeight: 600, fontSize: 29, color: TITLE_INK, marginBottom: 5 }}>
            Create
          </div>
          <div style={{ font: '400 15px/1.4 -apple-system, BlinkMacSystemFont, sans-serif', color: DESC_INK, marginBottom: 18 }}>
            Share something, or ask the founders.
          </div>
          {CARDS.map((card, i) => (
            <a
              key={card.key}
              ref={el => { itemRefs.current[i] = el }}
              role="menuitem"
              href={cardHref(card)}
              onClick={onCardClick(card)}
              className="flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#57241F]"
              style={{
                gap: 14,
                padding: 12,
                borderRadius: 16,
                backgroundColor: CARD_HOVER_BG,
                marginBottom: i < CARDS.length - 1 ? 10 : 0,
              }}
            >
              <div style={{ width: 58, height: 58, borderRadius: 12, overflow: 'hidden', flex: 'none', backgroundColor: card.key === 'ask' ? 'transparent' : PREVIEW_BG }}>
                {previewFor(card.key, true)}
              </div>
              <div className="flex-1 min-w-0">
                <div style={{ font: '600 17px/1.2 -apple-system, BlinkMacSystemFont, sans-serif', color: TITLE_INK }}>{card.title}</div>
                <div style={{ font: '400 14px/1.35 -apple-system, BlinkMacSystemFont, sans-serif', color: DESC_INK, marginTop: 3 }}>{card.sheetSubtitle}</div>
              </div>
              <svg width="9" height="15" viewBox="0 0 9 15" aria-hidden="true">
                <path d="M1.5 1.5 7 7.5l-5.5 6" stroke="#B7AF9E" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </a>
          ))}
        </div>
      </div>
    </div>
  )
}
